import { COLORS } from '@/config/colors';
import { css, fillOutlined, circle, shade } from './draw-utils';
import { darkCore, dangerHalo, hostile, spikes } from './bullets-danger';
import type { ProceduralDef } from '../types';

/** Player basic bullet — +X-facing cream capsule, cyan tail. */
export const bulletPlayerBasic: ProceduralDef = {
  key: 'bullet/player/basic',
  width: 48,
  height: 24,
  anchorX: 0.5,
  anchorY: 0.5,
  frames: 1,
  draw(ctx, w, h) {
    const cy = h / 2;
    // Tail glow
    const grad = ctx.createLinearGradient(2, 0, w * 0.5, 0);
    grad.addColorStop(0, 'rgba(69, 217, 197, 0)');
    grad.addColorStop(1, 'rgba(69, 217, 197, 0.9)');
    ctx.beginPath();
    ctx.ellipse(w * 0.3, cy, w * 0.3, h * 0.26, 0, 0, Math.PI * 2);
    ctx.fillStyle = grad;
    ctx.fill();
    // Muzzle-side halo so additive blending blooms nicely (G8)
    const halo = ctx.createRadialGradient(w * 0.62, cy, 2, w * 0.62, cy, h * 0.5);
    halo.addColorStop(0, 'rgba(240, 234, 214, 0.5)');
    halo.addColorStop(1, 'rgba(240, 234, 214, 0)');
    ctx.beginPath();
    ctx.ellipse(w * 0.62, cy, h * 0.5, h * 0.45, 0, 0, Math.PI * 2);
    ctx.fillStyle = halo;
    ctx.fill();

    // Capsule body
    ctx.beginPath();
    ctx.roundRect(w * 0.35, cy - 6, w * 0.55, 12, 6);
    fillOutlined(ctx, COLORS.playerCream, 3.5);

    // Tip highlight
    circle(ctx, w * 0.82, cy, 3.5);
    ctx.fillStyle = css(shade(COLORS.playerCream, 1.2));
    ctx.fill();
  },
};

/** Seeker missile — finned dart with an exhaust flare (docs/12 L3); bomb magenta. */
export const bulletPlayerMissile: ProceduralDef = {
  key: 'bullet/player/missile',
  width: 56,
  height: 26,
  anchorX: 0.5,
  anchorY: 0.5,
  frames: 1,
  draw(ctx, w, h) {
    const cy = h / 2;
    // Exhaust flare
    const grad = ctx.createLinearGradient(0, 0, w * 0.4, 0);
    grad.addColorStop(0, 'rgba(255, 92, 184, 0)');
    grad.addColorStop(1, 'rgba(255, 92, 184, 0.85)');
    ctx.beginPath();
    ctx.ellipse(w * 0.22, cy, w * 0.2, h * 0.16, 0, 0, Math.PI * 2);
    ctx.fillStyle = grad;
    ctx.fill();

    // Tail fins
    ctx.beginPath();
    ctx.moveTo(w * 0.34, cy);
    ctx.lineTo(w * 0.42, cy - 9);
    ctx.lineTo(w * 0.52, cy - 4);
    ctx.lineTo(w * 0.52, cy + 4);
    ctx.lineTo(w * 0.42, cy + 9);
    ctx.closePath();
    fillOutlined(ctx, COLORS.weaponBomb, 3);

    // Body tube + warhead
    ctx.beginPath();
    ctx.roundRect(w * 0.42, cy - 5, w * 0.4, 10, 5);
    fillOutlined(ctx, COLORS.playerCream, 3);
    ctx.beginPath();
    ctx.moveTo(w * 0.8, cy - 5);
    ctx.lineTo(w * 0.94, cy);
    ctx.lineTo(w * 0.8, cy + 5);
    ctx.closePath();
    fillOutlined(ctx, COLORS.weaponBomb, 3);

    // Seeker eye
    circle(ctx, w * 0.62, cy, 2.6);
    ctx.fillStyle = css(COLORS.playerCyan);
    ctx.fill();
  },
};

/**
 * Enemy ball — the classic hostile orb in the danger language (docs/04):
 * orange rim, short serrated teeth, dark core with a hot ember.
 */
export const bulletEnemyBall: ProceduralDef = hostile('bullet/enemy/ball', 48, 48, (ctx, w, _h, k) => {
  const c = w / 2;
  const r = w / 2 - 8;
  dangerHalo(ctx, c, r * 0.6, w / 2, COLORS.dangerOrange, k);
  spikes(ctx, c, c, r * 0.8, r * 1.28, 8, Math.PI / 8, 0.22, COLORS.dangerOrange);
  circle(ctx, c, c, r);
  fillOutlined(ctx, COLORS.dangerOrange, 4);
  // Small dark pupil: mostly hot orange (the ring is a hollow red saw, the
  // void a black disc — the three stay apart at game scale)
  darkCore(ctx, c, c, r * 0.36, COLORS.enemyEmber);
});

/** Flame blob — Pyre's fireballs and burn patches: tongues + charcoal heart. */
export const bulletEnemyFire: ProceduralDef = hostile('bullet/enemy/fire', 52, 52, (ctx, w, _h, k, frame) => {
  const c = w / 2;
  const r = w / 2 - 9;
  dangerHalo(ctx, c, r * 0.4, w / 2, COLORS.lavaOrange, k);
  // Flickering flame body — odd frames swap which tongue licks higher
  const odd = frame % 2 === 1;
  ctx.beginPath();
  ctx.moveTo(c - r, c + r * 0.3);
  ctx.quadraticCurveTo(c - r * 0.7, c - r * (odd ? 1.2 : 1), c, c - r * 0.5);
  ctx.quadraticCurveTo(c + r * 0.6, c - r * (odd ? 0.9 : 1.1), c + r, c + r * 0.3);
  ctx.quadraticCurveTo(c, c + r * 1.1, c - r, c + r * 0.3);
  ctx.closePath();
  fillOutlined(ctx, COLORS.dangerOrange, 4);
  darkCore(ctx, c, c + r * 0.15, r * 0.4, COLORS.enemyEmber);
});

/** Ring — the 2-damage heavy: red saw rim, six teeth, a big hollow core. */
export const bulletEnemyRing: ProceduralDef = hostile('bullet/enemy/ring', 48, 48, (ctx, w, _h, k) => {
  const c = w / 2;
  const r = w / 2 - 8;
  dangerHalo(ctx, c, r * 0.6, w / 2, COLORS.dangerRed, k);
  spikes(ctx, c, c, r * 0.85, r * 1.36, 6, 0, 0.3, COLORS.dangerRed);
  circle(ctx, c, c, r);
  fillOutlined(ctx, COLORS.dangerRed, 4.5);
  darkCore(ctx, c, c, r * 0.6, COLORS.enemyEmber);
});
